import {
	useCurrentCompanyId,
	useCurrentCompanyValue,
	useCurrentProfileType,
	useCurrentUser,
} from "hooks/auth";
import { useCurrentUserHasDashboard } from "hooks/featureFlags";
import { ClientPaths } from "newui/utils/navigation/ClientPaths";
import { GuestPaths } from "newui/utils/navigation/GuestPaths";
import { SupplierPaths } from "newui/utils/navigation/SupplierPaths";
import { useCallback, useMemo } from "react";
import { CompanyProfileType } from "../types/CompanyProfileType";

export interface NavigationPathsBase {
	clientPaths: ClientPaths | null;
	currentUserHomePage: string;
	guestPaths: GuestPaths | null;
	supplierPaths: SupplierPaths | null;
}
interface UseNavigationPaths extends NavigationPathsBase {
	getHomePageForProfileType: (
		profileType: CompanyProfileType,
		isAGuest?: boolean
	) => string;
}

export function useNavigationPaths(): UseNavigationPaths {
	const currentProfileType = useCurrentProfileType();

	const { isGuest } = useCurrentUser();
	const currentCompanyId = useCurrentCompanyId();
	const enableRfx = useCurrentCompanyValue("enable_rfx_functionality") ?? false;
	const hasDashboard = useCurrentUserHasDashboard();
	const navigationPaths = useMemo(() => {
		const allPaths = {
			clientPaths: null,
			guestPaths: null,
			supplierPaths: null,
		};

		if (currentCompanyId != null && !isNaN(currentCompanyId)) {
			allPaths.clientPaths = new ClientPaths(
				currentCompanyId,
				hasDashboard,
				enableRfx
			);
			allPaths.supplierPaths = new SupplierPaths(currentCompanyId);
		}

		if (isGuest) {
			allPaths.guestPaths = new GuestPaths();
		}

		return allPaths;
	}, [currentCompanyId, isGuest, enableRfx, hasDashboard]);

	const getHomePageForProfileType = useCallback(
		(profileType: CompanyProfileType, isAGuest = false): string => {
			const login = "/login";

			if (isAGuest) {
				return navigationPaths.guestPaths.home ?? login;
			}

			if (
				profileType === CompanyProfileType.CLIENT &&
				navigationPaths.clientPaths
			) {
				return navigationPaths.clientPaths.home ?? login;
			} else if (
				profileType === CompanyProfileType.SUPPLIER &&
				navigationPaths.supplierPaths
			) {
				return navigationPaths.supplierPaths.home ?? login;
			}

			return login;
		},
		[navigationPaths]
	);

	const currentUserHomePage = useMemo(() => {
		return getHomePageForProfileType(currentProfileType, isGuest);
	}, [currentProfileType, isGuest, getHomePageForProfileType]);

	return useMemo(
		() => ({
			...navigationPaths,
			currentUserHomePage,
			getHomePageForProfileType,
		}),
		[navigationPaths, currentUserHomePage, getHomePageForProfileType]
	);
}
